import { mdiFilter, mdiMagnify } from '@mdi/js';
import * as React from 'react';
import { ActiveFiltersBadges } from './ActiveFiltersBadges';
import { Button } from './Button';
import { CardBox } from './CardBox';
import { Icon } from './Icon';

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterField {
  key: string;
  label: string;
  options: FilterOption[];
}

export interface ActiveFilter {
  label: string;
  value: string;
  color: 'blue' | 'green' | 'yellow' | 'purple' | 'red';
}

export interface FilterBarProps {
  searchPlaceholder: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  filters: FilterField[];
  filterValues: Record<string, string>;
  onFilterChange: (key: string, value: string) => void;
  onClearAll: () => void;
  activeFilters: ActiveFilter[];
}

export const FilterBar: React.FC<FilterBarProps> = ({
  searchPlaceholder,
  searchValue,
  onSearchChange,
  filters,
  filterValues,
  onFilterChange,
  onClearAll,
  activeFilters,
}) => {
  const gridCols =
    filters.length === 1
      ? 'md:grid-cols-2'
      : filters.length === 2
        ? 'md:grid-cols-3'
        : 'md:grid-cols-4';

  return (
    <CardBox className='p-6'>
      <div className='space-y-4'>
        <div className='flex items-center space-x-4'>
          <div className='flex-1'>
            <div className='relative'>
              <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                <Icon path={mdiMagnify} size={20} className='text-grey-400' />
              </div>
              <input
                type='text'
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={e => onSearchChange(e.target.value)}
                className='w-full pl-10 pr-4 py-2 border border-grey-300 rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              />
            </div>
          </div>
          <div className='flex items-center space-x-2'>
            <Icon path={mdiFilter} size={20} className='text-grey-500' />
            <span className='text-sm font-medium text-grey-700'>Filters:</span>
          </div>
        </div>

        <div className={`grid grid-cols-1 ${gridCols} gap-4`}>
          {filters.map(filter => (
            <div key={filter.key}>
              <label className='block text-sm font-medium text-grey-700 mb-1'>
                {filter.label}
              </label>
              <select
                value={filterValues[filter.key] || ''}
                onChange={e => onFilterChange(filter.key, e.target.value)}
                className='w-full px-3 py-2 border border-grey-300 rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              >
                {filter.options.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ))}

          <div className='flex items-end'>
            <Button
              type='button'
              label='Clear Filters'
              color='light'
              onClick={onClearAll}
              className='w-full'
            />
          </div>
        </div>

        <ActiveFiltersBadges filters={activeFilters} />
      </div>
    </CardBox>
  );
};
